import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { Document, Page } from 'react-pdf'
import {
  useIngestionOcrResult,
  type IngestionTimelinePhase,
} from '@/shared/hooks/api/ingestionDiagnostics'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  formatDiagnosticsTimeOnly,
  getDiagnosticsTimezoneLabel,
  groupByDiagnosticsDay,
  severityDotClass,
} from '../utils/formatDiagnosticsTime'
import { formatJobEventDetails } from '../utils/formatJobEventDetails'
import {
  OCR_RESULT_PENDING_MESSAGE,
  isOcrResultPending,
} from '../utils/ocrResultPending'
import { OcrCallStatsSummary } from './OcrCallStatsSummary'

type IngestionTimelineProps = {
  phases: IngestionTimelinePhase[]
}

function PhaseEvents({
  phase,
  defaultExpanded,
}: {
  phase: IngestionTimelinePhase
  defaultExpanded: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const events = phase.events ?? []

  if (events.length === 0) {
    return null
  }

  return (
    <div className='mx-3 mb-3 overflow-hidden rounded-md border bg-background'>
      <button
        type='button'
        className='flex w-full items-center justify-between border-b bg-accent/40 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
        onClick={() => setExpanded((value) => !value)}
      >
        <span>Job events ({events.length})</span>
        <span>{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div>
          {events.map((event) => {
            const summary = formatJobEventDetails(event.eventType, event.details)
            return (
            <div
              key={`${event.at}-${event.eventType}-${JSON.stringify(event.details)}`}
              className='grid grid-cols-[92px_12px_1fr] items-start gap-2.5 border-b px-2.5 py-2 last:border-b-0 hover:bg-accent/30'
            >
              <div className='font-mono text-[11px] text-muted-foreground'>
                {formatDiagnosticsTimeOnly(event.at)}
              </div>
              <div
                className={`mt-1.5 size-2 rounded-full ${severityDotClass(event.severity)}`}
              />
              <div>
                <div className='text-xs font-semibold'>{event.eventType}</div>
                {summary && (
                  <div className='text-[11px] text-muted-foreground'>{summary}</div>
                )}
                {event.actor && (
                  <div className='text-[11px] text-muted-foreground'>{event.actor}</div>
                )}
              </div>
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function IngestionTimeline({ phases }: IngestionTimelineProps): ReactElement {
  const timezoneLabel = useMemo(() => getDiagnosticsTimezoneLabel(), [])

  return (
    <div className='space-y-4'>
      <p className='text-xs text-muted-foreground'>Times in {timezoneLabel}</p>
      {phases.map((phase) => {
        const groupedItems = groupByDiagnosticsDay(phase.items)
        const phaseToneClass =
          phase.tone === 'failure'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-primary/10 text-primary'
        const showOcrStats = phase.id === 'rag' && (phase.events?.length ?? 0) > 0
        const eventsDefaultExpanded = phase.id !== 'rag'

        return (
          <section
            key={phase.id}
            className='overflow-hidden rounded-lg border bg-card'
          >
            <header
              className={`flex items-center justify-between border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wide ${phaseToneClass}`}
            >
              <span>{phase.label}</span>
              {phase.summary && (
                <span className='text-[10px] font-medium normal-case opacity-80'>
                  {phase.summary}
                </span>
              )}
            </header>

            {groupedItems.map((group) => (
              <div key={group.day} className='border-b last:border-b-0'>
                <div className='px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
                  {group.day}
                </div>
                {group.items.map((item) => (
                  <div
                    key={`${item.at}-${item.title}`}
                    className='grid grid-cols-[92px_12px_1fr] items-start gap-2.5 px-3 py-2'
                  >
                    <div className='font-mono text-[11px] text-muted-foreground'>
                      {formatDiagnosticsTimeOnly(item.at)}
                    </div>
                    <div
                      className={`mt-1.5 size-2 rounded-full ${severityDotClass(item.severity)}`}
                    />
                    <div>
                      <div className='text-sm font-medium'>{item.title}</div>
                      {item.description && (
                        <div className='text-xs text-muted-foreground'>{item.description}</div>
                      )}
                      {item.code && (
                        <Badge variant='destructive' className='mt-1 font-mono text-[10px]'>
                          {item.code}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {showOcrStats && phase.events && (
              <OcrCallStatsSummary events={phase.events} />
            )}

            <PhaseEvents phase={phase} defaultExpanded={eventsDefaultExpanded} />
          </section>
        )
      })}
    </div>
  )
}

type IngestionPageInspectorProps = {
  sourceId: string
  pageNumber: number
  sourceUrl?: string | null
  sourceType: string
  pageMeta?: {
    status: string
    page_type?: string | null
    n_vlm_calls?: number | null
    cost_estimate_usd?: number | null
    duration_ms?: number | null
  }
  pageCount?: number
  onSelectAdjacentPage?: (pageNumber: number) => void
  adjacentPageNumbers?: {
    prev?: number
    next?: number
  }
  ocrResultAvailable?: boolean
}

export function IngestionPageInspector({
  sourceId,
  pageNumber,
  sourceUrl,
  sourceType,
  pageMeta,
  pageCount,
  onSelectAdjacentPage,
  adjacentPageNumbers,
  ocrResultAvailable = true,
}: IngestionPageInspectorProps): ReactElement {
  const ocrResultQuery = useIngestionOcrResult(sourceId, {
    enabled: ocrResultAvailable,
  })

  const extractedText = useMemo(() => {
    if (!ocrResultQuery.data) {
      return null
    }
    const pages = ocrResultQuery.data.pages ?? []
    const match =
      pages.find((page) => page.page_number === pageNumber) ??
      pages.find((page) => page.page === pageNumber)
    if (!match) {
      return null
    }
    if (typeof match.text === 'string') {
      return match.text
    }
    return JSON.stringify(match.content ?? match, null, 2)
  }, [ocrResultQuery.data, pageNumber])

  const resultPending =
    !ocrResultAvailable || isOcrResultPending(ocrResultQuery.error)

  const extractError = useMemo(() => {
    if (resultPending) {
      return null
    }
    if (ocrResultQuery.isError) {
      return ocrResultQuery.error instanceof Error
        ? ocrResultQuery.error.message
        : 'Failed to load extraction'
    }
    if (ocrResultQuery.isSuccess && extractedText === null) {
      return 'No extracted content for this page in result JSON'
    }
    return null
  }, [
    extractedText,
    ocrResultQuery.error,
    ocrResultQuery.isError,
    ocrResultQuery.isSuccess,
    resultPending,
  ])

  const isPdf = sourceType.toLowerCase().includes('pdf') || sourceUrl?.includes('.pdf')
  const isImage =
    sourceType.toLowerCase().includes('image') ||
    /\.(png|jpe?g|webp|gif)(\?|$)/i.test(sourceUrl ?? '')

  const showLoading =
    !resultPending &&
    !extractError &&
    extractedText === null &&
    ocrResultQuery.isFetching

  const showPrevPage = adjacentPageNumbers?.prev != null
  const showNextPage = adjacentPageNumbers?.next != null
  const showPageNavigation =
    pageCount != null && pageCount > 0 && onSelectAdjacentPage != null

  const previewRef = useRef<HTMLDivElement>(null)
  const [previewWidth, setPreviewWidth] = useState(0)
  useEffect(() => {
    const element = previewRef.current
    if (element == null) {
      return
    }
    const updateWidth = () => setPreviewWidth(Math.floor(element.clientWidth))
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [sourceUrl, isPdf])

  return (
    <div className='flex h-full min-h-0 min-w-0 flex-col gap-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex flex-wrap items-center gap-2'>
          <h3 className='text-sm font-semibold'>Page {pageNumber}</h3>
          {pageMeta && (
            <>
              <Badge variant='outline'>{pageMeta.status}</Badge>
              {pageMeta.page_type && (
                <span className='text-xs text-muted-foreground'>{pageMeta.page_type}</span>
              )}
              {pageMeta.n_vlm_calls != null && (
                <span className='text-xs text-muted-foreground'>
                  {pageMeta.n_vlm_calls} VLM calls
                </span>
              )}
              {pageMeta.cost_estimate_usd != null && (
                <span className='text-xs text-muted-foreground'>
                  ${pageMeta.cost_estimate_usd.toFixed(4)}
                </span>
              )}
              {pageMeta.duration_ms != null && (
                <span className='text-xs text-muted-foreground'>
                  {pageMeta.duration_ms} ms
                </span>
              )}
            </>
          )}
        </div>
        {showPageNavigation && (
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              disabled={!showPrevPage}
              onClick={() => {
                if (adjacentPageNumbers?.prev != null) {
                  onSelectAdjacentPage(adjacentPageNumbers.prev)
                }
              }}
            >
              ← Prev
            </Button>
            <span className='text-xs text-muted-foreground'>
              {pageNumber} / {pageCount}
            </span>
            <Button
              variant='outline'
              size='sm'
              disabled={!showNextPage}
              onClick={() => {
                if (adjacentPageNumbers?.next != null) {
                  onSelectAdjacentPage(adjacentPageNumbers.next)
                }
              }}
            >
              Next →
            </Button>
          </div>
        )}
      </div>

      <div className='grid min-h-0 min-w-0 flex-1 gap-4 lg:grid-cols-2'>
        <div className='min-h-[240px] min-w-0 overflow-hidden rounded-md border bg-muted/20 p-2'>
          {!sourceUrl && <p className='p-2 text-sm text-muted-foreground'>Source URL unavailable</p>}
          {sourceUrl && isPdf && (
            <div ref={previewRef} className='w-full overflow-hidden'>
              <Document file={sourceUrl} loading={<p className='p-2 text-sm'>Loading PDF…</p>}>
                {previewWidth > 0 && (
                  <Page
                    pageNumber={pageNumber}
                    width={previewWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                )}
              </Document>
            </div>
          )}
          {sourceUrl && isImage && (
            <img src={sourceUrl} alt={`Source page ${pageNumber}`} className='max-h-[520px] w-full object-contain' />
          )}
          {sourceUrl && !isPdf && !isImage && (
            <iframe title='Source document' src={sourceUrl} className='h-[520px] w-full' />
          )}
        </div>

        <div className='min-h-[240px] min-w-0 overflow-auto rounded-md border bg-background p-3'>
          <h4 className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Extracted content
          </h4>
          {resultPending && (
            <p className='text-sm text-muted-foreground'>{OCR_RESULT_PENDING_MESSAGE}</p>
          )}
          {extractError && <p className='text-sm text-destructive'>{extractError}</p>}
          {showLoading && (
            <p className='text-sm text-muted-foreground'>Loading extraction…</p>
          )}
          {extractedText && (
            <pre className='max-h-[520px] overflow-auto whitespace-pre-wrap text-xs'>
              {extractedText}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}
